import React, { useState } from 'react';
import { partitionComponents, isComponent, useTheme, ThemeProvider } from '@redsift/design-system';
import { PopoverContent } from '../popover-content';
import { PopoverTrigger } from '../popover-trigger';

import { PopoverContext } from './context';
import { PopoverPlacement, PopoverProps } from './types';
import { usePopover } from './usePopover';

const COMPONENT_NAME = 'Popover';
const CLASSNAME = 'redsift-popover';

/**
 * Popover displays contextual content anchored to a trigger element.
 * Built on Floating UI for intelligent positioning and collision detection.
 *
 * Unlike Tooltip (hover-triggered), Popover is click/tap-triggered and
 * can contain interactive content like forms or menus.
 *
 * Placements: `top`, `top-start`, `top-end`, `bottom`, `bottom-start`,
 * `bottom-end`, `left`, `left-start`, `left-end`, `right`, `right-start`,
 * `right-end`
 *
 * @example
 * // Basic popover
 * <Popover>
 *   <Popover.Trigger>
 *     <Button>Show Info</Button>
 *   </Popover.Trigger>
 *   <Popover.Content>
 *     <Text>Additional details here...</Text>
 *   </Popover.Content>
 * </Popover>
 *
 * @example
 * // Controlled popover with placement and width
 * <Popover
 *   placement="right-start"
 *   isOpen={showPopover}
 *   onOpen={setShowPopover}
 *   width={300}
 * >
 *   <Popover.Trigger><IconButton icon={mdiHelp} /></Popover.Trigger>
 *   <Popover.Content>
 *     <Heading level={3}>Help</Heading>
 *     <Text>Click here to learn more.</Text>
 *   </Popover.Content>
 * </Popover>
 *
 * @example
 * // Modal popover (blocks background interaction)
 * <Popover isModal>
 *   <Popover.Trigger><Button>Open Modal</Button></Popover.Trigger>
 *   <Popover.Content>This blocks interaction behind it</Popover.Content>
 * </Popover>
 *
 * @example
 * // With offset and custom sizing
 * <Popover offset={10} minWidth={200} maxWidth={400}>
 *   {...}
 * </Popover>
 */
export const BasePopover: React.FC<PopoverProps> & {
  displayName?: string;
  className?: string;
} = (props) => {
  const {
    children,
    color,
    defaultOpen,
    isModal = false,
    isOpen,
    maxWidth,
    minWidth,
    offset,
    onOpen,
    overrideDisplayName,
    placement = PopoverPlacement.bottom,
    role = 'dialog',
    theme: propsTheme,
    triggerClassName,
    width,
  } = props;

  const theme = useTheme(propsTheme);
  const [hideInsteadOfClose, setHideInsteadOfClose] = useState(false);

  const popover = usePopover({
    color,
    defaultOpen,
    isModal,
    isOpen,
    maxWidth,
    minWidth,
    offset,
    onOpen,
    placement,
    role,
    triggerClassName,
    setHideInsteadOfClose,
    hideInsteadOfClose,
    width,
  });

  const [[trigger], [content]] = partitionComponents(React.Children.toArray(children), [
    isComponent(overrideDisplayName?.trigger ?? 'PopoverTrigger'),
    isComponent(overrideDisplayName?.content ?? 'PopoverContent'),
  ]);

  return (
    <ThemeProvider value={{ theme }}>
      <PopoverContext.Provider value={popover}>
        {trigger && isComponent(overrideDisplayName?.trigger ?? 'PopoverTrigger')(trigger) ? trigger : null}
        {content && isComponent(overrideDisplayName?.content ?? 'PopoverContent')(content) ? content : null}
      </PopoverContext.Provider>
    </ThemeProvider>
  );
};
BasePopover.className = CLASSNAME;
BasePopover.displayName = COMPONENT_NAME;

export const Popover = Object.assign(BasePopover, {
  Trigger: PopoverTrigger,
  Content: PopoverContent,
});
